import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Activity, Plus, Pencil, Trash2 } from "lucide-react";

const ACTION_META = {
  create: { label: "Created", color: "#0A7029", Icon: Plus },
  update: { label: "Updated", color: "#002FA7", Icon: Pencil },
  delete: { label: "Deleted", color: "#C50F1F", Icon: Trash2 },
};

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [limit, setLimit] = useState("200");

  const load = async () => {
    try {
      const { data } = await api.get(`/history/timeline?limit=${limit}`);
      setEvents(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, [limit]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return events.filter((e) => {
      if (action !== "all" && e.action !== action) return false;
      if (!ql) return true;
      return (
        (e.employee_name || "").toLowerCase().includes(ql) ||
        (e.actor_email || "").toLowerCase().includes(ql)
      );
    });
  }, [events, q, action]);

  const grouped = useMemo(() => {
    const out = {};
    for (const e of filtered) {
      const day = new Date(e.ts).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
      (out[day] = out[day] || []).push(e);
    }
    return out;
  }, [filtered]);

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Compliance · Activity
        </p>
        <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          Audit log
        </h1>
        <p className="mt-2 max-w-xl text-sm text-neutral-600">
          Every create / update / delete on an employee is recorded. Use this to investigate, audit, or report on changes.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input
            placeholder="Search employee or actor…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none border-neutral-300 pl-9"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[160px] rounded-none border-neutral-300" data-testid="audit-action-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="create">Created</SelectItem>
            <SelectItem value="update">Updated</SelectItem>
            <SelectItem value="delete">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={limit} onValueChange={setLimit}>
          <SelectTrigger className="w-[140px] rounded-none border-neutral-300"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="50">Last 50</SelectItem>
            <SelectItem value="200">Last 200</SelectItem>
            <SelectItem value="1000">Last 1000</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-neutral-200 bg-white">
        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Activity size={28} className="mx-auto mb-3 text-neutral-300"/>
            <p className="text-sm text-neutral-600">No activity yet. Edit an employee to see entries appear here.</p>
          </div>
        ) : (
          <ul>
            {Object.entries(grouped).map(([day, items]) => (
              <li key={day}>
                <div className="border-b border-neutral-100 bg-neutral-50 px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  {day}
                </div>
                <ul>
                  {items.map((e, i) => {
                    const meta = ACTION_META[e.action] || ACTION_META.update;
                    const Icon = meta.Icon;
                    return (
                      <li key={i} className="flex items-center gap-3 border-b border-neutral-100 px-6 py-3 last:border-0" data-testid={`audit-row-${e.action}-${i}`}>
                        <span className="flex h-7 w-7 items-center justify-center" style={{ background: meta.color }}>
                          <Icon size={13} className="text-white"/>
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">
                            <span className="font-medium">{e.actor_email || "system"}</span>
                            <span className="text-neutral-500"> {meta.label.toLowerCase()} </span>
                            <span className="font-medium">{e.employee_name || "(unknown)"}</span>
                          </p>
                          <p className="truncate text-[10px] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                            {new Date(e.ts).toLocaleTimeString()} · {e.employee_id?.slice(0, 8)}
                          </p>
                        </div>
                        <Badge variant="outline" className="rounded-none border-neutral-300 text-[10px] uppercase">
                          {e.action}
                        </Badge>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
